import { useEffect, useRef, useState } from "react";

type Tone = "info" | "success" | "error" | "warning";

export interface PopupOpts {
  title?: string;
  body?: string;
  tone?: Tone;
}

interface PopupItem extends PopupOpts {
  id: number;
  confirm?: boolean;
  confirmLabel?: string;
  danger?: boolean;
  resolve?: (v: boolean) => void;
}

let seq = 0;
let items: PopupItem[] = [];
const listeners = new Set<(v: PopupItem[]) => void>();
const emit = () => listeners.forEach((l) => l(items));

// Императивная замена alert(): popup("текст") или popup({title, body, tone}).
// Не-confirm рендерится ТОСТОМ в углу с автозакрытием (успех не надо кликать,
// не перекрывает контент); confirm — модалкой по центру.
export function popup(input: string | PopupOpts): void {
  const o: PopupOpts = typeof input === "string" ? { body: input } : input;
  items = [...items, { id: ++seq, tone: "info", ...o }];
  emit();
}

// Замена confirm(): await confirmPopup({...}) → true/false.
export function confirmPopup(
  input: string | (PopupOpts & { confirmLabel?: string; danger?: boolean }),
): Promise<boolean> {
  const o = typeof input === "string" ? { body: input } : input;
  return new Promise((resolve) => {
    items = [...items, { id: ++seq, tone: o.danger ? "warning" : "info", confirm: true, ...o, resolve }];
    emit();
  });
}

const dismiss = (id: number, result = false) => {
  const it = items.find((i) => i.id === id);
  items = items.filter((i) => i.id !== id);
  emit();
  it?.resolve?.(result);
};

const TONE: Record<Tone, { icon: string; ring: string; accent: string; bar: string }> = {
  info: { icon: "ℹ️", ring: "ring-brand/25", accent: "text-brand", bar: "bg-brand/60" },
  success: { icon: "✅", ring: "ring-emerald-500/25", accent: "text-emerald-300", bar: "bg-emerald-500/60" },
  error: { icon: "⛔", ring: "ring-rose-500/30", accent: "text-rose-300", bar: "bg-rose-500/60" },
  warning: { icon: "⚠️", ring: "ring-amber-500/30", accent: "text-amber-300", bar: "bg-amber-500/60" },
};

// Сколько держим тост перед автозакрытием (ошибку — дольше, чтобы успели прочесть).
const TOAST_MS: Record<Tone, number> = { info: 4000, success: 3500, warning: 6000, error: 8000 };

function Toast({ item }: { item: PopupItem }) {
  const tone = TONE[item.tone ?? "info"];
  return (
    <div
      className={`relative w-80 max-w-[90vw] overflow-hidden rounded-xl card-elev ring-1 ring-inset ${tone.ring} p-3.5 shadow-2xl pointer-events-auto animate-[fadeIn_.15s_ease-out]`}
      role="status"
    >
      <div className="flex items-start gap-2.5">
        <span className="text-base leading-none mt-0.5">{tone.icon}</span>
        <div className="min-w-0 flex-1">
          {item.title && <div className={`font-semibold text-sm ${tone.accent}`}>{item.title}</div>}
          {item.body && (
            <div className="text-xs text-zinc-300 whitespace-pre-wrap break-words leading-relaxed max-h-[40vh] overflow-auto mt-0.5">
              {item.body}
            </div>
          )}
        </div>
        <button
          onClick={() => dismiss(item.id)}
          className="text-zinc-500 hover:text-white text-xs leading-none mt-0.5"
          aria-label="Закрыть"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function ConfirmDialog({ item }: { item: PopupItem }) {
  const tone = TONE[item.tone ?? "info"];
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => dismiss(item.id)} />
      <div
        className={`relative w-full max-w-md rounded-2xl card-elev ring-1 ring-inset ${tone.ring} p-5 shadow-2xl`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start gap-3">
          <span className="text-xl leading-none mt-0.5">{tone.icon}</span>
          <div className="min-w-0 flex-1">
            {item.title && <div className={`font-semibold mb-1.5 ${tone.accent}`}>{item.title}</div>}
            {item.body && (
              <div className="text-sm text-zinc-300 whitespace-pre-wrap break-words leading-relaxed max-h-[50vh] overflow-auto">
                {item.body}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={() => dismiss(item.id, false)}
            className="h-8 px-4 text-xs font-semibold rounded-lg bg-zinc-900 text-zinc-300 border border-zinc-800 hover:bg-zinc-700 hover:text-white transition-all"
          >
            Отмена
          </button>
          <button
            onClick={() => dismiss(item.id, true)}
            className={`h-8 px-4 text-xs font-semibold rounded-lg border transition-all ${
              item.danger
                ? "bg-red-900/40 text-red-300 border-red-700/50 hover:bg-red-500 hover:text-white hover:border-red-500"
                : "bg-brand/15 text-brand border-brand/30 hover:bg-brand-grad hover:text-brand-fg hover:border-transparent"
            }`}
            autoFocus
          >
            {item.confirmLabel ?? "Подтвердить"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PopupHost() {
  const [list, setList] = useState<PopupItem[]>(items);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  useEffect(() => {
    listeners.add(setList);
    return () => {
      listeners.delete(setList);
    };
  }, []);

  const toasts = list.filter((i) => !i.confirm);
  // Только ВЕРХНИЙ confirm — модалка (confirm'ы строго последовательны).
  const dialog = [...list].reverse().find((i) => i.confirm);

  // Автозакрытие тостов (успех не надо кликать). Confirm НЕ автозакрываем.
  useEffect(() => {
    for (const it of toasts) {
      if (timers.current.has(it.id)) continue;
      const ms = TOAST_MS[it.tone ?? "info"];
      const h = setTimeout(() => {
        timers.current.delete(it.id);
        dismiss(it.id);
      }, ms);
      timers.current.set(it.id, h);
    }
    const liveIds = new Set(toasts.map((t) => t.id));
    for (const [id, h] of timers.current) {
      if (!liveIds.has(id)) {
        clearTimeout(h);
        timers.current.delete(id);
      }
    }
  }, [toasts]);

  // Клавиатура — только для активной модалки confirm.
  useEffect(() => {
    if (!dialog) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss(dialog.id, false);
      // Enter подтверждает только БЕЗОПАСНЫЕ диалоги; danger (снос/удаление) —
      // строго кликом, чтобы случайный Enter не снёс хост/ноду (аудит B4).
      else if (e.key === "Enter" && !dialog.danger) dismiss(dialog.id, true);
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [dialog]);

  return (
    <>
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
          {toasts.map((it) => (
            <Toast key={it.id} item={it} />
          ))}
        </div>
      )}
      {dialog && <ConfirmDialog item={dialog} />}
    </>
  );
}
